import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { ArrowRight } from './icons';
import { track } from '../lib/funnel';
import { ApiError } from '../lib/api';
import { revealContact, type ContactAccess } from '../lib/catalog-client';
import { plansPathWithContext } from '../lib/pending-checkout';
import { formatTashkentTime } from '../lib/format';
import { ContactSupport } from './contact-support';

/**
 * The metered tier's contact control — the missing half of the ИП plan.
 *
 * The backend grew a full reveal-and-meter mechanism (an endpoint, a monthly
 * allowance, a budget ceiling) and nothing on the web ever called it. A paying
 * ИП subscriber saw the identical «Оформите Pro» wall a free user sees, so the
 * five opens they had bought were unspendable. This is the control that spends
 * them.
 *
 * ## Why the button is sometimes deliberately absent
 *
 * Two states look like "you could click this" and are not:
 *
 *  - **Above the plan's budget ceiling.** The endpoint refuses before spending
 *    anything, so a button here would be a button that cannot work. The refusal
 *    is invisible until clicked, which is the worst possible time to learn it —
 *    so the ceiling is stated instead, with the upgrade that lifts it.
 *  - **Allowance exhausted.** Same reasoning: nothing to spend.
 *
 * In both cases the card converts rather than teases. `blockedByCeiling` and
 * `remaining` come from the same service that enforces them on write, so this
 * cannot drift into offering an action the server would reject.
 *
 * ## Two things an open can buy
 *
 * On most lots it is the заказчик contact. On a lot whose заказчик published
 * NONE — common on the donor (`ifi`) layer — it is the link to the original
 * notice on the source portal, which is otherwise Pro-only. That case used to
 * render no button at all, leaving a paying subscriber holding an allowance
 * they could not spend on exactly the lots where the link is the only way
 * through. `hasContact` picks the copy; `reveal` applies the same split.
 */
export function ContactRevealCard({
  tenderId,
  access,
  hasContact = true,
}: {
  tenderId: string;
  access: ContactAccess;
  /** False when all three contact columns are empty — the open then buys the
   *  source link only. Defaults TRUE, so a payload without the flag never
   *  downgrades the promise the button makes. */
  hasContact?: boolean;
}) {
  const t = useT();
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * /plans WITH the lot attached.
   *
   * These two CTAs were bare `<Link to="/plans">`, so the one thing the buyer
   * was here for did not survive the click. Measured 2026-09-18 over 30 days:
   * 75 of 98 new buyers had opened a specific tender in the hour before paying
   * and only 45 came back to it afterwards. `?tender` is what /plans stashes and
   * /billing/return spends. `src=paywall` also fills `plans_viewed.source`,
   * which these arrivals previously reported as 'direct'.
   */
  const plansTo = plansPathWithContext('paywall', tenderId);

  const exhausted = (access.remaining ?? 0) <= 0;
  const blocked = access.blockedByCeiling;
  /*
   * The per-account READ ceiling — a DIFFERENT state from everything else in
   * this card, and it has to win.
   *
   * Every branch below sells Pro, which is exactly wrong here: the only people
   * who can hit a read ceiling are people already entitled to the contact. An
   * «Оформите Pro» button shown to a Pro subscriber who has simply read a lot
   * today is the same mistake the ИП tier shipped with, in the other direction.
   *
   * So this state renders one calm paragraph and a way to reach a human, and
   * NOTHING else — no CTA, no `paywall_shown` (it is not a paywall; counting it
   * as one would pollute the denominator of the wall that actually converts).
   */
  const limited = access.limited ?? null;

  /*
   * The contacts wall, which had never emitted a single row.
   *
   * `contacts` was in the paywall vocabulary from the start and the free tier
   * allows ZERO contact opens, so this is the wall the most people hit — yet
   * only the CTA CLICK was recorded (`upgrade_cta_clicked`). With no impression
   * event there was no denominator, so the one wall that gates the product's
   * headline paid feature had no conversion rate at all.
   */
  useEffect(() => {
    // `!limited` is load-bearing, not defensive: a read ceiling is not a wall,
    // and recording one here would add impressions with no possible conversion
    // to the rate of the wall that does convert.
    if (!limited && (blocked || exhausted)) track('paywall_shown', { wall: 'contacts', tenderId });
  }, [blocked, exhausted, limited, tenderId]);

  async function onReveal() {
    setPending(true);
    setError(null);
    try {
      await revealContact(tenderId);
      // No browser-side event here on purpose. `contact_revealed` is recorded
      // by the endpoint itself: it is the paid action this tier sells, so it
      // belongs with `registered`/`paid` among the server-recorded events
      // rather than on the forgeable public beacon.
      // Refetch the tender rather than splicing the contact into the cache by
      // hand: the server decides `remaining` and `opened`, and a hand-patched
      // cache would show a stale count until the next navigation.
      //
      // ⚠️ Prefix ['catalog','tender',id] — NOT ['tender',id]. The detail query
      // is keyed `['catalog','tender', id, locale]`, so the shorter key matches
      // nothing and invalidation silently no-ops: the reveal would succeed, an
      // open would be spent, and the page would keep showing the button. The
      // locale is deliberately omitted so BOTH cached locales refresh.
      await qc.invalidateQueries({ queryKey: ['catalog', 'tender', tenderId] });
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'plan_budget_ceiling'
          ? t('tender.reveal.ceiling')
          : e instanceof ApiError && e.code === 'pro_required'
            ? t('tender.reveal.exhausted')
            : t('tender.reveal.error'),
      );
    } finally {
      setPending(false);
    }
  }

  const ctaClassName =
    'inline-flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-md bg-brand px-[18px] py-2.5 text-center text-[14px] font-bold leading-snug text-white transition-colors hover:bg-brand-mid disabled:pointer-events-none disabled:opacity-60';

  return (
    <div className="relative overflow-hidden rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
      <h2 className="mb-2 text-[15px] font-bold text-ink">
        {t(hasContact ? 'tender.reveal.title' : 'tender.reveal.titleNoContact')}
      </h2>

      {limited ? (
        <>
          <p className="text-[13px] leading-relaxed text-ink-soft">
            {limited.window === 'minute'
              ? t('tender.reveal.limitedMinute')
              : t('tender.reveal.limitedDay', {
                  limit: limited.limit,
                  // Falls back to the raw instant only if it is unparseable,
                  // which would mean the server sent something malformed.
                  time: formatTashkentTime(limited.resetAt) ?? limited.resetAt,
                })}
          </p>
          <ContactSupport
            // Pre-tagged `subscription`: this is a paid-feature question, and
            // the intake routes on the category.
            defaultCategory="subscription"
            className="mt-2 text-[13px] font-semibold text-brand hover:underline"
          >
            {t('tender.reveal.limitedSupport')}
          </ContactSupport>
        </>
      ) : blocked ? (
        <>
          <p className="mb-3 text-[13px] leading-relaxed text-ink-soft">
            {t('tender.reveal.ceiling')}
          </p>
          <Link
            to={plansTo}
            onClick={() => track('upgrade_cta_clicked', { source: 'tenderRevealCeiling' })}
            className={ctaClassName}
          >
            {t('tender.cta.pro')}
            <ArrowRight className="h-3.5 w-3.5 shrink-0" />
          </Link>
        </>
      ) : exhausted ? (
        <>
          <p className="mb-3 text-[13px] leading-relaxed text-ink-soft">
            {t('tender.reveal.exhausted')}
          </p>
          <Link
            to={plansTo}
            onClick={() => track('upgrade_cta_clicked', { source: 'tenderRevealExhausted' })}
            className={ctaClassName}
          >
            {t('tender.cta.pro')}
            <ArrowRight className="h-3.5 w-3.5 shrink-0" />
          </Link>
        </>
      ) : (
        <>
          {/* Say what the open buys BEFORE it is spent. On a contact-less lot
              that is the source link and nothing else, and a subscriber who
              learns that only afterwards has been charged a surprise. */}
          {!hasContact && (
            <p className="mb-2 text-[13px] leading-relaxed text-ink-soft">
              {t('tender.reveal.noContactHint')}
            </p>
          )}
          <p className="mb-3 text-[13px] leading-relaxed text-ink-soft">
            {t('tender.reveal.remaining', { count: access.remaining ?? 0 })}
          </p>
          <button type="button" onClick={onReveal} disabled={pending} className={ctaClassName}>
            {pending
              ? t('tender.reveal.pending')
              : t(hasContact ? 'tender.reveal.action' : 'tender.reveal.actionNoContact')}
          </button>
          {error && (
            <p className="mt-3 text-[12.5px] text-status-closing-text" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
